import { QueryClient, type QueryKey, type UseQueryResult, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { seconds } from "@/3514/utils/time.utils"

import { ESessionScheduleQueryKey as EQueryKey } from "./session-schedule.modal.api.config"
import fetchers from "./session-schedule.modal.api.queries.fetchers"
import type {
    ISessionScheduleCoachTimeWidgetQueryDto as ICoachTimeWidgetQueryDto,
    TSessionScheduleErrorPayload as TErrorPayload
} from "./session-schedule.modal.api.types"

type TCoachTimeWidgetQueryParams = { coachId: number | null; sessionId: number; participantId: number }

type TCoachTimeWidgetQuery = UseQueryResult<ICoachTimeWidgetQueryDto, AxiosError<TErrorPayload>>

function useCoachTimeWidgetQuery(
    params: TCoachTimeWidgetQueryParams
): ReturnType<(params: TCoachTimeWidgetQueryParams) => TCoachTimeWidgetQuery> {
    return useQuery<ICoachTimeWidgetQueryDto, AxiosError<TErrorPayload>, ICoachTimeWidgetQueryDto, QueryKey>({
        queryKey: EQueryKey.CoachTimeWidget(params),
        queryFn: fetchers.coachTimeWidget,
        retry: 1,
        retryDelay: seconds(3),
        staleTime: seconds(15),
        gcTime: seconds(15),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        networkMode: "offlineFirst",
        enabled: true,
        throwOnError: true,
        experimental_prefetchInRender: true,
        notifyOnChangeProps: ["data", "isFetching", "isFetched", "isSuccess", "isError", "error"]
    })
}

async function coachTimeWidgetQueryPrefetcher(
    { qc }: { qc: QueryClient },
    params: TCoachTimeWidgetQueryParams
): Promise<void> {
    await qc.prefetchQuery<ICoachTimeWidgetQueryDto, AxiosError<TErrorPayload>, ICoachTimeWidgetQueryDto, QueryKey>({
        queryKey: EQueryKey.CoachTimeWidget(params),
        queryFn: fetchers.coachTimeWidget,
        staleTime: seconds(15),
        gcTime: seconds(15),
        retry: false
    })
}

async function coachTimeWidgetQueryRemover(
    { qc }: { qc: QueryClient },
    params: TCoachTimeWidgetQueryParams
): Promise<void> {
    await qc.cancelQueries({
        queryKey: EQueryKey.CoachTimeWidget(params),
        exact: true
    })
    await qc.resetQueries({
        queryKey: EQueryKey.CoachTimeWidget(params),
        exact: true
    })
}

export { useCoachTimeWidgetQuery as useSessionScheduleCoachTimeWidgetQuery }
export { coachTimeWidgetQueryPrefetcher, coachTimeWidgetQueryRemover }
export type { TCoachTimeWidgetQuery as TSessionScheduleCoachTimeWidgetQuery }
